import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Megaphone, Send, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

/**
 * OrganizeContest — public page hosting a simple contact form. Submissions
 * are stored in `contact_requests` and admins can follow up manually.
 */
export default function OrganizeContest() {
  usePageMeta({
    title: "Organiser un concours — QRISE",
    description:
      "Vous êtes un commerçant ? Lancez votre jeu-concours avec QRISE : 8 jeux interactifs, un QR code permanent et des leads qualifiés.",
  });
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  function set(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit() {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Nom, email et message sont obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/contact`, form);
      setDone(true);
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur";
      toast.error(typeof msg === "string" ? msg : "Erreur");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-background text-foreground px-6 py-16">
        <div className="max-w-md mx-auto text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-500/15 text-emerald-600 mb-4">
            <Check className="h-8 w-8" />
          </div>
          <h1 className="text-2xl font-bold">Message envoyé !</h1>
          <p className="mt-2 text-muted-foreground">
            Notre équipe vous recontactera sous 48h.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground px-6 py-12">
      <div className="max-w-xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour
        </Link>

        <div className="mt-8 text-center">
          <div
            className="inline-flex items-center justify-center h-14 w-14 rounded-2xl mb-4"
            style={{
              background:
                "linear-gradient(135deg,#8B5CF6 0%,#EC4899 100%)",
              color: "white",
            }}
          >
            <Megaphone className="h-7 w-7" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Organiser mon concours
          </h1>
          <p className="mt-3 text-muted-foreground">
            Parlez-nous de votre projet — on revient vers vous sous 48h.
          </p>
        </div>

        <div className="mt-8 qrise-card p-6 sm:p-8 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">Nom complet *</Label>
              <Input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                className="mt-1.5 rounded-xl h-10"
                data-testid="contact-name"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Email *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className="mt-1.5 rounded-xl h-10"
                data-testid="contact-email"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium">Entreprise</Label>
              <Input
                value={form.company}
                onChange={(e) => set("company", e.target.value)}
                className="mt-1.5 rounded-xl h-10"
                data-testid="contact-company"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">Téléphone</Label>
              <Input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="mt-1.5 rounded-xl h-10"
                data-testid="contact-phone"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium">Votre projet *</Label>
            <Textarea
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
              rows={5}
              className="mt-1.5 rounded-xl"
              placeholder="Dites-nous quel type de jeu vous aimeriez organiser, votre audience, vos objectifs…"
              data-testid="contact-message"
            />
          </div>
          <Button
            type="button"
            onClick={submit}
            disabled={submitting}
            className="w-full h-12 rounded-xl text-sm font-extrabold uppercase tracking-[0.18em]"
            style={{
              background:
                "linear-gradient(135deg,#8B5CF6 0%,#EC4899 100%)",
              color: "white",
            }}
            data-testid="contact-submit"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? "Envoi…" : "Envoyer ma demande"}
          </Button>
        </div>
      </div>
    </div>
  );
}
